/**
 * What the Gala Seating sidebar shows, worked out without React or Fabric so it
 * can be asserted on directly (jsdom cannot start in this checkout).
 *
 * Whether a guest is seated is always the Seat Assignment module's answer,
 * passed in as `seatedAt`, never a count of Chairs on the canvas.
 */

export type SidebarTab = 'build' | 'seat' | 'export';

export interface SidebarTicket {
  ticketId: string;
  name?: string;
}

export interface SidebarGuest {
  id: string;
  name?: string;
  email?: string;
  company?: string;
  tickets?: SidebarTicket[];
}

/** Where a Ticket is seated, or undefined when it is not. */
export type SeatedAt = (ticketId: string) => string | undefined;

/**
 * The Ticket a guest is seated by. Assignment already goes through the first
 * Ticket everywhere else in Gala Seating, so the sidebar reads the same one.
 */
export function guestTicket(guest: SidebarGuest): SidebarTicket | undefined {
  return guest.tickets?.find((ticket) => !!ticket?.ticketId);
}

export function isUnseated(guest: SidebarGuest, seatedAt: SeatedAt): boolean {
  const ticket = guestTicket(guest);
  return !!ticket && !seatedAt(ticket.ticketId);
}

export function unseatedCount(guests: SidebarGuest[], seatedAt: SeatedAt): number {
  return guests.reduce((count, guest) => (isUnseated(guest, seatedAt) ? count + 1 : count), 0);
}

/** The value of the "All" ticket filter. */
export const ALL_TICKETS = '';

/** Ticket names to filter by, in the order they first appear. */
export function ticketFilterOptions(guests: SidebarGuest[]): string[] {
  const names: string[] = [];
  guests.forEach((guest) => {
    const name = guestTicket(guest)?.name?.trim();
    if (name && !names.includes(name)) names.push(name);
  });
  return names;
}

export interface GuestFilter {
  query: string;
  ticket: string;
}

/** Unseated guests matching the search (name, email or company) and ticket. */
export function unseatedGuests(
  guests: SidebarGuest[],
  seatedAt: SeatedAt,
  { query, ticket }: GuestFilter,
): SidebarGuest[] {
  const needle = query.trim().toLowerCase();
  return guests.filter((guest) => {
    if (!isUnseated(guest, seatedAt)) return false;
    if (ticket !== ALL_TICKETS && guestTicket(guest)?.name?.trim() !== ticket) return false;
    if (!needle) return true;
    return [guest.name, guest.email, guest.company].some((field) =>
      field?.toLowerCase().includes(needle),
    );
  });
}

/**
 * Who "Seat next N" seats: the guests at the top of the list as it is searched
 * and filtered, one per open Chair. Narrowing the list first is how a Table is
 * filled with one company or one ticket type.
 */
export function nextToSeat(list: SidebarGuest[], openChairs: number): SidebarGuest[] {
  if (openChairs <= 0) return [];
  return list.filter((guest) => guestTicket(guest)).slice(0, openChairs);
}

/** The most Chairs a Table can have, matching the existing chair handler. */
export const MAX_CHAIRS = 20;

/**
 * The fewest Chairs a Table can be given. Never below the guests already
 * seated there, so shrinking a Table can not leave someone without a Chair.
 */
export function minChairs(seated: number): number {
  return Math.max(2, seated);
}

/**
 * The tab to show once a Table is picked on the canvas. Export has nothing to
 * say about one Table, so it hands over to Build; Build and Seat stay put.
 */
export function tabAfterTablePicked(tab: SidebarTab): SidebarTab {
  return tab === 'export' ? 'build' : tab;
}

export interface TableChip {
  tableId: string;
  label: string;
  seated: number;
  chairs: number;
}

/** Table Labels in order: numbers numerically (2 before 10), then by text. */
export function compareTableLabels(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
}

/** Tables in label order, numbers numerically (2 before 10), then by text. */
export function sortTableChips<T extends { label: string }>(tables: T[]): T[] {
  return [...tables].sort((a, b) => compareTableLabels(a.label, b.label));
}

export function seatingTotals(tables: TableChip[]): { seated: number; chairs: number } {
  return tables.reduce(
    (sum, table) => ({ seated: sum.seated + table.seated, chairs: sum.chairs + table.chairs }),
    { seated: 0, chairs: 0 },
  );
}
